import { useMemo, useState, type ChangeEvent } from 'react';
import { Button, Checkbox, Input, Separator } from '@predoc/cow';
import { cn } from '@/lib/utils';
import { OUTPUT_DOCUMENT_CATEGORIES } from '../constants/output-document-categories';
import type { OutputCategoryFilterState } from '../types';
import {
  clearOutputCategories,
  getIsOutputCategorySelected,
  getNextOutputCategoryFilter,
  selectAllOutputCategories,
} from '../utils/outputCategoryFilter';

interface EfaxOutputCategoryFilterListProps {
  categoryFilter: OutputCategoryFilterState;
  onCategoryFilterChange: (categoryFilter: OutputCategoryFilterState) => void;
  listClassName?: string;
  autoFocusSearch?: boolean;
}

export const EfaxOutputCategoryFilterList = ({
  categoryFilter,
  onCategoryFilterChange,
  listClassName,
  autoFocusSearch = false,
}: EfaxOutputCategoryFilterListProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      return [...OUTPUT_DOCUMENT_CATEGORIES];
    }

    return OUTPUT_DOCUMENT_CATEGORIES.filter((category) =>
      category.toLowerCase().includes(trimmed),
    );
  }, [searchTerm]);

  const handleCategoryChange = (category: string, checked: boolean) => {
    onCategoryFilterChange(
      getNextOutputCategoryFilter(categoryFilter, category, checked),
    );
  };

  return (
    <>
      <div className="p-2 pb-0">
        <Input
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(event.target.value)
          }
          className="h-8 text-sm"
          autoFocus={autoFocusSearch}
          aria-label="Search document categories"
        />
      </div>
      <div className={cn('overflow-y-auto', listClassName)}>
        {filteredCategories.length === 0 ? (
          <div className="flex items-center justify-center p-4">
            <span className="text-muted-foreground text-sm">
              No categories found.
            </span>
          </div>
        ) : (
          <div className="p-2">
            {filteredCategories.map((category) => {
              const isSelected = getIsOutputCategorySelected(
                categoryFilter,
                category,
              );

              return (
                <div key={category} className="flex items-center py-1">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked: boolean) =>
                      handleCategoryChange(category, checked)
                    }
                    label={<span className="text-sm">{category}</span>}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Separator />
      <div className="flex flex-row justify-start gap-2 p-2">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0"
          onClick={() => onCategoryFilterChange(selectAllOutputCategories())}
        >
          Select All
        </Button>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0"
          onClick={() => onCategoryFilterChange(clearOutputCategories())}
        >
          Select None
        </Button>
      </div>
    </>
  );
};
